import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge, Button, Popover, Separator } from "@/src/common/components/ui";
import { NOTIFICATION_ROUTES } from "../constants/notification-routes";
import {
	useGetNotificationsQuery,
	useGetUnreadCountQuery,
	useMarkAllAsReadMutation,
	useMarkAsReadMutation,
} from "../services/notification-http.service";
import { NotificationEmptyState } from "./notification-empty-state";
import { NotificationItem } from "./notification-item";

export function NotificationBell() {
	const { t } = useTranslation(["notification", "shell"]);
	const { data: countData } = useGetUnreadCountQuery();
	const { data: notificationsData } = useGetNotificationsQuery({ limit: 5 });
	const [markAsRead] = useMarkAsReadMutation();
	const [markAllAsRead] = useMarkAllAsReadMutation();

	const unreadCount = countData?.count ?? 0;
	const notifications = notificationsData?.notifications ?? [];

	const formatBadge = (count: number) => {
		if (count > 99) return "99+";
		return count.toString();
	};

	return (
		<Popover>
			<Popover.Trigger>
				{unreadCount > 0 ? (
					<Badge.Anchor>
						<Button isIconOnly variant="ghost" size="sm" aria-label={t("shell:header.notifications")}>
							<Bell size={20} />
						</Button>
						<Badge color="danger" size="sm">
							{formatBadge(unreadCount)}
						</Badge>
					</Badge.Anchor>
				) : (
					<Button isIconOnly variant="ghost" size="sm" aria-label={t("shell:header.notifications")}>
						<Bell size={20} />
					</Button>
				)}
			</Popover.Trigger>
			<Popover.Content placement="bottom" className="w-[380px]">
				<Popover.Dialog>
					<div className="flex items-center justify-between">
						<Popover.Heading className="text-sm font-semibold">{t("notification:page_title")}</Popover.Heading>
						<Button variant="ghost" size="sm" isDisabled={unreadCount === 0} onPress={() => markAllAsRead()} className="text-xs">
							{t("notification:mark_all_read")}
						</Button>
					</div>

					<Separator />

					<div className="max-h-[320px] overflow-y-auto">
						{notifications.length === 0 ? (
							<NotificationEmptyState variant="all-caught-up" />
						) : (
							notifications.map((notification) => (
								<NotificationItem key={notification.id} notification={notification} onMarkRead={(id) => markAsRead({ id })} />
							))
						)}
					</div>

					<Separator />

					<div className="pt-2">
						<Link to={NOTIFICATION_ROUTES.LIST} className="block text-center text-sm text-primary hover:underline">
							{t("notification:view_all")}
						</Link>
					</div>
				</Popover.Dialog>
			</Popover.Content>
		</Popover>
	);
}
